import { useState } from 'react';
import { useLoaderData } from 'react-router';
import { Check, ChevronDown } from 'lucide-react';

import { useTracker } from '@archipro-website/tracker';

import { cn } from '@rocco/utils/cn';
import { useVariantShipping } from '~/modules/product/hook/use-variant-shipping';
import { useVariantStock } from '~/modules/product/hook/use-variant-stock';
import { useVariantShippingMethods } from '@modules/ecommerce/context/VariantShippingMethodsContext';
import type { ItemShippingType } from '@modules/ecommerce/context/ItemShippingContext';
import { DrawerShippingPanel } from './DrawerShippingPanel';
import ReturnsPolicyTemplate from '@modules/product/component/refund-policy/RefundTemplate';
import type { RefundPolicyData } from '@modules/product/component/refund-policy/RefundPolicy';
import type { loader } from '~/routes/_app.product.$urlSegment';
import { GROUP_LABEL } from './MobileVariantSelector';

/** The label side of a row — what the value answers. */
const LABEL = 'shrink-0 font-sans text-[0.875rem] text-[#9b9b9b]';

/** The value side, ranged right against it. */
const VALUE = 'min-w-0 text-right font-sans text-[0.875rem] text-[#181818]';

/**
 * One fact about getting the thing: its label left, its answer right.
 *
 * Ranged apart rather than run together, so the four read as a table a buyer
 * can scan down rather than four sentences they have to read.
 */
const Row = ({
    label,
    onClick,
    children,
}: {
    label: string;
    /** Makes the whole row the control, not just the value in it. */
    onClick?: () => void;
    children: React.ReactNode;
}) => {
    const body = (
        <>
            <span className={LABEL}>{label}</span>
            <span className={VALUE}>{children}</span>
        </>
    );
    const shared =
        'flex w-full items-center justify-between gap-3 py-[0.375rem]';
    return onClick ? (
        <button
            type="button"
            onClick={onClick}
            className={cn(
                shared,
                'cursor-pointer border-0 bg-transparent px-0 text-left',
            )}
        >
            {body}
        </button>
    ) : (
        <div className={shared}>{body}</div>
    );
};

/**
 * The delivery estimate, without the sentence it arrives wrapped in.
 *
 * The API phrases it for a line of prose ("Estimated delivery in 3-5 days");
 * the row already says Est. Delivery, so the lead-in would be said twice.
 */
const estimateValue = (text: string) =>
    text.replace(/^estimated delivery\s+(in\s+)?/i, '').trim() || text;

interface DrawerShippingRowsProps {
    refundPolicyData?: RefundPolicyData;
}

/**
 * Availability, shipping, delivery and pickup as a short table, for the
 * drawer's closing block.
 *
 * The same facts the legacy StockAndShipping prints as prose, from the same
 * hooks — this is a second presentation of them, not a second source. Every
 * row drops out when the data behind it does.
 */
export const DrawerShippingRows = ({
    refundPolicyData,
}: DrawerShippingRowsProps) => {
    const { product, shoppingCartSummary } = useLoaderData<typeof loader>();
    const {
        hasClickAndCollect,
        hasShippingType,
        shipping,
        shippingMethods,
        setShipping,
    } = useVariantShipping(false);
    const { stockDescription, canAddToCart, showAsInStock, hasLeadTime } =
        useVariantStock({ hasClickAndCollect, hasShippingType });
    const { isLoading } = useVariantShippingMethods();
    const [shippingOpen, setShippingOpen] = useState(false);
    const [policyOpen, setPolicyOpen] = useState(false);
    const tracker = useTracker();

    // The same event the dialog logged when it opened — the policy is still
    // being read, it just is not a dialog any more.
    const togglePolicy = () => {
        setPolicyOpen((open) => {
            if (!open) {
                tracker.log('ReturnsRefundsClick', {
                    url: new URL(window.location.href),
                    targetTracker: 'archiproTracker',
                });
            }
            return !open;
        });
    };

    const region =
        (shoppingCartSummary as { Region?: { Title: string } | null }).Region
            ?.Title ?? null;
    const shippingMethod =
        shipping?.type === 'delivery'
            ? shippingMethods.find((method) => method.ID === shipping.ID)
            : undefined;
    const pickupLocations = product.Professional?.PickupLocations ?? [];
    const pickupLocation =
        shipping?.type === 'clickAndCollect'
            ? pickupLocations.find((location) => location.ID === shipping.ID)
            : undefined;

    /**
     * The one line for how the order travels: the method, not the branch —
     * "Click & Collect" rather than the shop's name, which is on the row below
     * and in the panel. Otherwise what delivery costs, or where it is going
     * when the supplier quotes that separately and there is no figure yet.
     */
    const shippingValue = pickupLocation
        ? 'Click & Collect'
        : shippingMethod
          ? // Named when there is no figure to quote: a method whose cost text
            // is empty, or one the supplier quotes separately on a cart with
            // no region yet, otherwise left the row with nothing to say — and
            // this row is the only way back into the picker, so it took the
            // choice away from the reader who had already made one.
            (shippingMethod.QuotedSeparately
                ? region
                : shippingMethod.ShippingCostText) || shippingMethod.Title
          : null;

    const deliveryEstimate = pickupLocation
        ? pickupLocation.PickupEstimateText
        : shippingMethod?.DeliveryEstimateText;

    return (
        <>
            <div className="flex flex-col">
                {stockDescription ? (
                    <Row label="Availability">
                        <span className="inline-flex items-center gap-[0.375rem]">
                            {showAsInStock ? (
                                <span
                                    aria-hidden="true"
                                    className="size-[0.5rem] shrink-0 rounded-full bg-[#1a9c5b]"
                                />
                            ) : null}
                            {stockDescription}
                        </span>
                    </Row>
                ) : null}

                {/* One row for the choice, checked: delivery and pickup are
                    the two answers to the same question, and listing pickup
                    separately as "Available" said what could be had rather
                    than what was picked. The whole row opens the picker — the
                    chevron says so, but the row is the target. */}
                {shippingValue && !isLoading ? (
                    <Row
                        label="Shipping"
                        onClick={() =>
                            canAddToCart && setShippingOpen((open) => !open)
                        }
                    >
                        <span className="inline-flex items-center gap-[0.25rem]">
                            <Check
                                aria-hidden="true"
                                className="size-[0.875em] shrink-0"
                                strokeWidth={2.5}
                            />
                            {shippingValue}
                            <ChevronDown
                                aria-hidden="true"
                                className={cn(
                                    'size-[0.875em] transition-transform',
                                    shippingOpen && 'rotate-180',
                                )}
                                strokeWidth={2.5}
                            />
                        </span>
                    </Row>
                ) : null}

                {/* The picker opens under the row that summarises it, rather
                    than over the drawer: the reader is choosing between costs
                    and dates that the rows above and below are quoting. */}
                {shippingOpen ? (
                    <div className="py-[0.75rem]">
                        <DrawerShippingPanel
                            shipping={shipping}
                            onChange={(next: ItemShippingType) => {
                                tracker.log('ShippingOptionSelect', {
                                    url: new URL(window.location.href),
                                    targetTracker: 'archiproTracker',
                                    data: { ExtraData: next.ID },
                                });
                                setShipping(next);
                            }}
                            onClose={() => setShippingOpen(false)}
                        />
                    </div>
                ) : null}

                {deliveryEstimate ? (
                    <Row label="Est. Delivery">
                        {estimateValue(deliveryEstimate)}
                        {hasLeadTime ? ', after lead time' : null}
                    </Row>
                ) : null}
            </div>

            {refundPolicyData ? (
                <div className="mt-8 flex flex-col gap-[0.25rem] border-t border-black/10 pt-5">
                    <p className={GROUP_LABEL}>Return &amp; Refunds</p>
                    {/* The policy opens in place rather than over the drawer:
                        a reader checking it is mid-decision, and a modal takes
                        the configuration they are checking it against off the
                        screen. */}
                    <button
                        type="button"
                        aria-expanded={policyOpen}
                        onClick={togglePolicy}
                        // In the label grey, not the value ink: it names the
                        // thing rather than answering it, as Est. Delivery does
                        // on the left of its row.
                        className={cn(
                            LABEL,
                            'flex w-full cursor-pointer items-center justify-between gap-3 border-0 bg-transparent p-0 text-left',
                        )}
                    >
                        <span className="hover:underline hover:underline-offset-2">
                            {policyOpen ? 'Hide policy' : 'View policy'}
                        </span>
                        <ChevronDown
                            aria-hidden="true"
                            className={cn(
                                'size-[0.875em] transition-transform',
                                policyOpen && 'rotate-180',
                            )}
                            strokeWidth={2.5}
                        />
                    </button>
                    {policyOpen ? (
                        <div className="pt-[0.5rem]">
                            <ReturnsPolicyTemplate data={refundPolicyData} />
                        </div>
                    ) : null}
                </div>
            ) : null}
        </>
    );
};
